import styled, { css } from 'styled-components';

import { BlankButton } from 'basics/buttons';

import { flexAllCenter, flexColumn, respondDown } from 'styles/mixins';
import { Breakpoints, COLORS, FONT_SIZE, Z_INDEX } from 'styles/style-constants';

export const Slot = styled.div<{ $height?: number }>`
    height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
`;

export const Container = styled.div<{ $fullscreen: boolean }>`
    ${flexColumn};
    gap: 1.6rem;
    min-width: 0;
    padding: 2.4rem;
    border-radius: 2.4rem;
    background: ${COLORS.surface};

    ${respondDown(Breakpoints.sm)`
        padding: 1.6rem;
    `}

    ${({ $fullscreen }) =>
        $fullscreen &&
        css`
            position: fixed;
            inset: 0;
            z-index: ${Z_INDEX.modal};
            width: 100%;
            height: 100dvh;
            padding: max(0.8rem, env(safe-area-inset-top)) max(1.6rem, env(safe-area-inset-right))
                max(0.8rem, env(safe-area-inset-bottom)) max(1.6rem, env(safe-area-inset-left));
            border-radius: 0;
            gap: 0.8rem;
            overflow: hidden;
        `}
`;

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-shrink: 0;
`;

export const Tabs = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
    padding: 1.6rem 0;
    overflow-x: auto;

    [data-fullscreen='true'] & {
        padding: 0;
    }

    ${respondDown(Breakpoints.md)`
        display: none;
    `}
`;

export const MobileChartPicker = styled.div`
    display: none;
    position: relative;
    flex: 1;
    min-width: 0;
    max-width: 24rem;
    margin-right: auto;

    > svg {
        position: absolute;
        top: 1.2rem;
        right: 1.2rem;
        pointer-events: none;
    }

    ${respondDown(Breakpoints.md)`
        display: flex;
    `}
`;

export const MobileChartSelect = styled.select`
    appearance: none;
    width: 100%;
    min-width: 0;
    height: 4rem;
    padding: 0 4rem 0 1.2rem;
    border: 0.1rem solid ${COLORS.gray100};
    border-radius: 1.2rem;
    background: ${COLORS.surface};
    color: ${COLORS.textTertiary};
    ${FONT_SIZE.sm};
    cursor: pointer;
`;

export const FullscreenButton = styled(BlankButton)<{ $fullscreen: boolean }>`
    display: none;
    flex-shrink: 0;
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    background: ${COLORS.gray50};

    ${respondDown(Breakpoints.md)`
        ${flexAllCenter};
    `}

    ${({ $fullscreen }) => $fullscreen && flexAllCenter}
`;

export const RotateHint = styled.div`
    ${FONT_SIZE.sm};
    color: ${COLORS.textGray};
    text-align: center;

    @media (orientation: landscape) {
        display: none;
    }
`;

export const Panels = styled.div<{ $fullscreen: boolean }>`
    min-height: 0;
    height: 50.6rem;
    ${({ $fullscreen }) =>
        $fullscreen &&
        css`
            flex: 1;
            height: auto;
        `}
`;

export const Tab = styled(BlankButton)<{ $active: boolean }>`
    flex: 1 0 0;
    min-width: max-content;
    padding: 0.4rem 1.2rem 1.2rem;
    &,
    &:focus {
        border: 0;
        border-bottom: 0.2rem solid
            ${({ $active }) => ($active ? COLORS.purple500 : COLORS.gray100)};
    }
    color: ${({ $active }) => ($active ? COLORS.textTertiary : COLORS.textGray)};
    ${FONT_SIZE.md};
    line-height: 1.8rem;
    text-align: center;
    white-space: nowrap;

    &:hover {
        color: ${COLORS.purple500};
    }

    &:focus-visible {
        outline: 0.2rem solid ${COLORS.cyan500};
        outline-offset: -0.2rem;
    }
`;

export const ChartPanel = styled.div`
    min-width: 0;
    height: 100%;
`;

export const HistoryChart = styled.div`
    height: 100%;
    border-radius: 1.2rem;
    background: ${COLORS.gray50};
`;
